import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * One language at a time, chosen by the person.
 *
 * Karigar-facing screens used to print every line twice — Hindi, then
 * English — which doubled the text on the smallest screens in the product.
 * The person now picks a language once (from their profile, or the switch on
 * the public sign-in and registration screens) and every screen shows only
 * that one.
 *
 * Until they pick, each screen uses its own `fallback`: Hindi where karigars
 * are the audience (D-11), English for staff.
 *
 * The choice lives in this browser only (localStorage). That is a per-device
 * convenience, deliberately: it needs no account, so it already works on the
 * sign-in screen, before anyone is known.
 */
export type Lang = "en" | "hi";

/** A string in both languages — the shape the karigar copy is written in. */
export interface Bilingual {
  hi: string;
  en: string;
}

const KEY = "gatiq_lang";
/** The Performance Tracker's own switch stored its choice here first. */
const LEGACY_KEY = "gatiq_admin_lang";

function readStored(): Lang | null {
  try {
    const v = localStorage.getItem(KEY) ?? localStorage.getItem(LEGACY_KEY);
    return v === "hi" || v === "en" ? v : null;
  } catch {
    // Private mode or blocked storage: fall back per screen, every visit.
    return null;
  }
}

interface LanguageState {
  chosen: Lang | null;
  setLang: (lang: Lang) => void;
}

const LanguageContext = createContext<LanguageState>({ chosen: null, setLang: () => undefined });

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [chosen, setChosen] = useState<Lang | null>(readStored);

  const setLang = useCallback((lang: Lang) => {
    setChosen(lang);
    try {
      localStorage.setItem(KEY, lang);
    } catch {
      // The choice still holds for this visit.
    }
  }, []);

  const value = useMemo(() => ({ chosen, setLang }), [chosen, setLang]);
  return <LanguageContext value={value}>{children}</LanguageContext>;
}

/**
 * The language to render in, plus helpers:
 *   t("नमस्ते", "Hello")   → one of the two
 *   pick({ hi, en })       → the same, for copy already stored as a pair
 */
export function useLanguage(fallback: Lang = "en") {
  const { chosen, setLang } = use(LanguageContext);
  const lang = chosen ?? fallback;

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback((hi: string, en: string) => (lang === "hi" ? hi : en), [lang]);
  const pick = useCallback((pair: Bilingual) => pair[lang], [lang]);

  return { lang, setLang, t, pick };
}

/** `en-IN` / `hi-IN`, for dates and numbers. */
export function localeOf(lang: Lang): string {
  return lang === "hi" ? "hi-IN" : "en-IN";
}
